/**
 * ActivationComposerScreen — adaptateur primaire du composeur (route `activation/composer`).
 *
 * Fil mince : le contexte (lien ou portail) + le brouillon + le view-model →
 * `<ActivationComposerPage>`.
 *
 * Les trois ouvreurs arrivent du pont legacy, qui monte les modales restées legacy (grille de
 * créneaux, catalogue de matières, comparatif des formules) : le legacy importe les ports, jamais
 * l'inverse. Le thème est porté ici pour la même raison que sur l'arrivée — l'écran est public,
 * monté hors du navigateur parent.
 */
import { useSelector } from "react-redux"

import { ActivationComposerPage } from "../../../../../../design-system"
import { activationComposerViewModel } from "../../../../view-models/parent/Activation/activationComposerViewModel"
import { readActivationLinkParams, type ActivationRoute } from "./activationLink"
import { useActivationComposerData } from "./useActivationComposerData"
import { useActivationComposerState, type ActivationPickerOpeners } from "./useActivationComposerState"
import { useParentThemeScope } from "../../../hooks/useParentThemeScope"

export type ActivationComposerScreenProps = ActivationPickerOpeners & { route?: ActivationRoute }

export function ActivationComposerScreen(props: ActivationComposerScreenProps) {
    useParentThemeScope()

    const linkParams = readActivationLinkParams(props.route)
    const data = useActivationComposerData(linkParams)
    const { draft, feedback, actions } = useActivationComposerState({
        linkParams,
        seed: data.seed,
        soldNoCommitmentHours: data.soldNoCommitmentHours,
        seedKey: data.seedKey,
        isModification: data.isModification,
        onOpenSlots: props.onOpenSlots,
        onOpenSubjects: props.onOpenSubjects,
        onOpenOfferInfo: props.onOpenOfferInfo,
    })
    const vm = useSelector((state: unknown) =>
        activationComposerViewModel(state, { raw: data.raw, fromLink: data.fromLink, draft })
    )

    return (
        <ActivationComposerPage
            editing={vm.editing}
            loading={vm.loading}
            classTitle={vm.classTitle}
            classOptions={vm.classOptions}
            selectedClass={vm.selectedClass}
            onSelectClass={actions.selectClass}
            hoursOptions={vm.hoursOptions}
            selectedHours={vm.selectedHours}
            onSelectHours={actions.selectHours}
            hoursNote={vm.hoursNote}
            selectedSubjects={vm.selectedSubjects}
            onOpenSubjects={() => actions.openSubjects(vm.subjectsPicker)}
            allocationRows={vm.allocationRows}
            onChangeAllocation={actions.changeAllocation}
            slotsCount={vm.slotsCount}
            slotsSummary={vm.slotsSummary}
            onOpenSlots={() => actions.openSlots(vm.slotsPicker)}
            resumeDateOptions={vm.resumeDateOptions}
            resumeDateMin={vm.resumeDateMin}
            selectedResumeDate={vm.selectedResumeDate}
            onSelectResumeDate={actions.selectResumeDate}
            lockedResumeDateLine={vm.lockedResumeDateLine}
            lockedResumeDateNote={vm.lockedResumeDateNote}
            offerOptions={vm.offerOptions}
            selectedOffer={vm.selectedOffer}
            onSelectOffer={actions.selectOffer}
            onOpenOfferInfo={actions.openOfferInfo}
            commitmentOptions={vm.commitmentOptions}
            withCommitment={vm.withCommitment}
            onChangeCommitment={actions.changeCommitment}
            guarantees={vm.guarantees}
            summary={vm.summary}
            submitting={vm.submitting}
            onSubmit={() => actions.submit(vm.submission)}
            error={feedback.error}
            onDismissError={actions.dismissError}
            success={feedback.success}
            onDismissSuccess={actions.dismissSuccess}
            onBack={actions.goBack}
        />
    )
}

export default ActivationComposerScreen
